import React from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const SystemHealthMonitor = ({ healthData, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8]?.map(i => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (value, thresholds = { good: 70, warning: 85 }) => {
    if (value <= thresholds?.good) return 'text-success';
    if (value <= thresholds?.warning) return 'text-warning';
    return 'text-error';
  };

  const metrics = [
    {
      label: 'CPU',
      value: `${healthData?.cpuUsage}%`,
      icon: 'Cpu',
      status: getStatusColor(healthData?.cpuUsage),
      progress: healthData?.cpuUsage
    },
    {
      label: 'Memória',
      value: `${healthData?.memoryUsage}%`,
      icon: 'HardDrive',
      status: getStatusColor(healthData?.memoryUsage),
      progress: healthData?.memoryUsage
    },
    {
      label: 'Disco',
      value: `${healthData?.diskUsage}%`,
      icon: 'Database',
      status: getStatusColor(healthData?.diskUsage),
      progress: healthData?.diskUsage
    },
    {
      label: 'Usuários Ativos',
      value: healthData?.activeUsers,
      icon: 'Users',
      status: 'text-primary',
      progress: null
    },
    {
      label: 'Requisições/min',
      value: healthData?.requestsPerMinute,
      icon: 'Activity',
      status: 'text-primary',
      progress: null
    },
    {
      label: 'Tempo Resposta',
      value: `${healthData?.averageResponseTime}ms`,
      icon: 'Clock',
      status: getStatusColor(healthData?.averageResponseTime, { good: 200, warning: 500 }),
      progress: null
    },
    {
      label: 'Taxa de Erro',
      value: `${healthData?.errorRate}%`,
      icon: 'AlertTriangle',
      status: getStatusColor(healthData?.errorRate, { good: 1, warning: 3 }),
      progress: null
    },
    {
      label: 'Uptime',
      value: `${healthData?.uptime}%`,
      icon: 'CheckCircle',
      status: 'text-success',
      progress: healthData?.uptime
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-muted/50 px-6 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Monitoramento de Saúde do Sistema
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Performance da aplicação e utilização de recursos em tempo real
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
          <span className="text-sm text-success font-medium">Online</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {metrics?.map((metric, index) => (
            <div
              key={index}
              className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <Icon name={metric?.icon} size={20} className={metric?.status} />
                <span className={cn("text-xl font-bold", metric?.status)}>
                  {metric?.value}
                </span>
              </div>
              <div className="text-sm text-muted-foreground mb-2">
                {metric?.label}
              </div>
              {metric?.progress !== null && (
                <div className="bg-muted rounded-full h-1.5">
                  <div
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      metric?.progress <= 70 ? 'bg-success' :
                      metric?.progress <= 85 ? 'bg-warning' : 'bg-error'
                    )}
                    style={{ width: `${metric?.progress}%` }}
                  ></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* System Status Messages */}
        <div className="mt-6 space-y-3">
          <div className="flex items-start space-x-3 p-4 bg-success/5 rounded-lg border border-success/20">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">
                Sistema operando normalmente
              </div>
              <div className="text-xs text-muted-foreground">
                Todos os serviços estão funcionando conforme esperado. Última verificação: {new Date()?.toLocaleTimeString('pt-BR')}
              </div>
            </div>
          </div>

          {healthData?.cpuUsage > 70 && (
            <div className="flex items-start space-x-3 p-4 bg-warning/5 rounded-lg border border-warning/20">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-foreground mb-1">
                  Alta utilização de CPU detectada
                </div>
                <div className="text-xs text-muted-foreground">
                  A utilização de CPU está acima do normal ({healthData?.cpuUsage}%). Considere otimizar processos.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthMonitor;